import { create, useStore, type StoreApi } from 'zustand';
import { resolvePinStyle, type PinStyleKey } from '@/lib/pinStyles';

// ─── Types ──────────────────────────────────────────────────────────────────

export type WizardMode = 'sponsor' | 'festival' | 'listing';

export type CreativeMode =
  | 'ai_generated'
  | 'admin_authored'
  | 'client_image'
  | 'client_full_bleed'
  | 'client_video';

export type CreativeTab = 'ai' | 'upload' | 'gallery';

export type AspectRatio = 'banner' | 'square' | 'tall';

/**
 * The four high-level "campaign type" cards shown on Step 1 of the wizard.
 * Each one maps to a single template (or a small family of templates),
 * a default placement, and a recommended creative aspect ratio. The
 * wizard uses this enum to drive everything downstream — the upload
 * dropzone's expected aspect, the targeting defaults, and the live
 * preview surface — so a non-technical operator just picks the visual
 * card that matches the deal they sold.
 */
export type CampaignTypeKey =
  | 'sponsored_map_pin'
  | 'passenger_home_banner'
  | 'commuter_banner_morning'
  | 'commuter_banner_evening'
  | 'ride_complete_takeover'
  | 'weather_alert'
  | 'festival_takeover'
  | 'launch_splash'
  | 'passenger_sheet_idle'
  | 'passenger_sheet_confirm'
  | 'passenger_sheet_active_ride';

export interface BusinessEntry {
  id: string;
  name: string;
  slug: string;
  category: string | null;
  slogan: string | null;
  description: string | null;
  logo_url: string | null;
  hero_image_url: string | null;
  brand_color_primary: string | null;
  brand_color_accent: string | null;
  latitude: number | null;
  longitude: number | null;
  address_line: string | null;
  village: string | null;
  parish: string | null;
  phone: string | null;
  email: string | null;
  website_url: string | null;
  whatsapp: string | null;
  facebook_url: string | null;
  instagram_handle: string | null;
  hours_summary: string | null;
  is_verified: boolean;
  is_featured: boolean;
  score?: number;
}

export interface SponsorTemplate {
  key: string;
  label: string;
  description: string;
  emoji: string;
  placement: string;
  placement_friendly: string;
  estimated_daily_impressions: number;
  is_festival?: boolean;
}

export interface AiGeneration {
  id: string;
  imageUrl: string;
  prompt: string;
  aspectRatio: AspectRatio;
  timestamp: number;
}

export interface WizardState {
  // Navigation
  step: number;

  // Step 0 (prerequisite) — Brand
  selectedBusiness: BusinessEntry | null;
  newBusinessOpen: boolean;
  newBusinessStep: number; // 0=identity, 1=brand, 2=location

  // Step 1 — Campaign type
  // The wizard now supports MULTIPLE templates per "deal". Each item in
  // campaignTypes will be published as its own row in ad_campaigns
  // (sharing the same brand / creative / schedule / business). The
  // single `campaignType` field is kept as the "primary" template (the
  // first one in the array) so legacy reads (preview defaults, hour
  // window guard, validation) keep working.
  campaignType: CampaignTypeKey | '';
  campaignTypes: CampaignTypeKey[];
  // Package picked on Step 1 (see lib/campaignPackages). Cleared as soon
  // as the operator changes the placement set by hand.
  packageKey: string | null;
  // Pitch sample: only the preview team (Sponsors → Preview team) sees it.
  teamPreviewOnly: boolean;

  // Resolved template key (the "primary" type — every campaign type
  // maps to exactly one sponsor_templates row in the DB). Kept on the
  // store so existing downstream UI keeps working.
  templateKey: string;
  templatePlacement: string;
  templateLabel: string;
  estimatedDailyImpressions: number;

  // Step 3 — Creative
  creativeTab: CreativeTab;
  creativeMode: CreativeMode;
  // AI tab
  brandBrief: string;
  headline: string;
  body: string;
  ctaText: string;
  /** Optional deal code riders show at the counter. */
  offerCode: string;
  offerTerms: string;
  backgroundColor: string;
  aiCopyLoading: boolean;
  // AI art
  artStyleHint: string;
  artAspectRatio: AspectRatio;
  artLoading: boolean;
  artImageUrl: string;
  recentGenerations: AiGeneration[];
  // Upload tab
  uploadImageUrl: string;
  uploadVideoUrl: string;
  clientSubOption: 'logo' | 'full_bleed' | 'video';
  // Gallery
  gallerySelectedLogoUrl: string;

  // Step 3 — Targeting Engine
  // Hour-of-day window (24h, 0–24). 0..24 means "all day". The dual-thumb
  // slider in TargetingStep writes both values at once.
  hourStart: number;
  hourEnd: number;
  // Selected corridor IDs (matches `corridors.id` TEXT primary key in DB).
  // Empty array means "all corridors".
  corridorIds: string[];
  // Festival mode toggle. When ON, the wizard rewrites the placement to
  // `festival_event_takeover` and unlocks event-specific fields.
  festivalMode: boolean;
  eventSlug: string;
  eventLocationLabel: string;
  audience: string;
  // Weather conditions this campaign should fire on (e.g. ['rain'] for an
  // umbrella promo). Matches the labels the resolver understands. Empty =
  // no weather filter. Persisted to ad_campaigns.weather_triggers.
  weatherTriggers: string[];
  // Proximity radius (km) for location-targeted campaigns. Empty string =
  // platform default (8 km) when the campaign has a geo-anchor, or stays
  // island-wide when it has none. Persisted to ad_campaigns.target_radius_km.
  targetRadiusKm: string;

  // Step 4 — Schedule (auto-kill switch)
  startDate: string;     // ISO yyyy-mm-dd
  endDate: string;       // ISO yyyy-mm-dd (computed from durationDays but editable)
  durationDays: number;  // kept in sync with endDate; surfaced to RPC
  festivalQuickPick: string;

  // Pricing (advanced — settable via Step 4 expandable panel)
  bidXcd: string;
  cpmRateXcd: string;
  flatFeeXcd: string;
  maxImpressionsPerDayPerUser: number;
  coolDownHours: number;
  maxDailyImpressions: string;
  invoiceStatus: string;
  invoiceAmountXcd: string;

  // Step 5 — Publish
  pushAfterPublish: boolean;
  publishing: boolean;

  // ─── Sponsored Map Pin position override ───────────────────────────
  // Operators sometimes need the pin to sit somewhere different from
  // the company's registered Google Maps coordinates — e.g. the brand's
  // mailing address points to head office but the actual customer-facing
  // shop is across the street, or the directory entry has stale coords.
  // Both fields default to the business's registered lat/lng when a
  // brand is picked, but the operator can drag the pin in the live
  // preview to override either value (or both). When `pinLatOverride`
  // is null we fall back to `selectedBusiness.latitude` at publish time.
  // For brands with NO registered coordinates, the override starts at
  // Roseau center (15.301, -61.388) so the wizard always has *something*
  // to publish.
  pinLatOverride: number | null;
  pinLngOverride: number | null;

  // ─── Sponsored Map Pin style ───────────────────────────────────────
  // Distinguished pins: each business type gets a category-specific
  // marker (icon + colour). When `pinStyle` is null the mobile client
  // derives a default from the listing's `category`. When set, it
  // overrides the auto-derive — useful for bespoke pitches (e.g. a
  // bookstore that wants to show as a "cafe" pin because they sell
  // coffee). Defaults to whatever `resolvePinStyle(business)` returns
  // when the operator picks a brand.
  pinStyle: PinStyleKey | null;

  // ─── Festival linkage (mode === 'festival' only) ───────────────────
  // When the festival wizard's first step picks a real festival from
  // the directory + a sponsorship tier, those choices are stored here
  // and used to:
  //   - auto-prefill creative (hero image, theme color)
  //   - auto-prefill targeting (corridors, audience)
  //   - auto-prefill schedule (festival start/end window)
  //   - drive the publish step to call admin_link_festival_sponsorship
  // For the legacy sponsor flow these fields stay null.
  selectedFestival: FestivalEntry | null;
  festivalTier: FestivalTierKey | null;
  festivalSubEventSlug: string | null;     // for tier='stage' / 'sub_event'
  contractedAmountXcd: string;             // free-text input in EC$
  invoiceNumber: string;                   // optional, free-text
}

// ─── Festival types ───────────────────────────────────────────────────────

export interface FestivalSubEvent {
  slug: string;
  name: string;
  starts_at: string;
  ends_at: string;
  venue_name?: string | null;
  venue_lat?: number | null;
  venue_lng?: number | null;
}

export interface FestivalTierConfig {
  tier: FestivalTierKey;
  label: string;
  slot_count: number;
  rate_xcd_min: number;
  rate_xcd_max: number;
  perks: string[];
}

export type FestivalTierKey =
  | 'title'
  | 'stage'
  | 'corridor'
  | 'venue_partner'
  | 'after_party'
  | 'sub_event';

export interface FestivalEntry {
  id: string;
  slug: string;
  name: string;
  starts_at: string;
  ends_at: string;
  organizer_name: string | null;
  external_url: string | null;
  hero_image_url: string | null;
  theme_color: string;
  emoji: string | null;
  description: string | null;
  venue_name: string | null;
  venue_lat: number | null;
  venue_lng: number | null;
  corridor_ids: string[];
  sub_events: FestivalSubEvent[];
  sponsorship_tiers: FestivalTierConfig[];
  is_recurring: boolean;
  is_active: boolean;
  days_until_start?: number;
  is_live?: boolean;
  // Optional extras populated by admin_list_festivals
  sponsorships?: Array<{
    id: string;
    tier: FestivalTierKey;
    sub_event_slug: string | null;
    contracted_amount_xcd: number | null;
    invoice_status: string;
    invoice_number: string | null;
    campaign_id: string;
    brand_name: string;
    logo_url: string | null;
    is_paused: boolean;
    created_at: string;
    // Underlying ad_campaigns schedule + active flag, surfaced by
    // admin_list_festivals (see migration
    // 20260620690000_admin_campaign_preview_and_festival_mgmt.sql).
    // Drives the schedule chip + management actions on the Festivals
    // page sponsor stack. All four are optional so the type stays
    // backwards-compatible if an older RPC version is in flight.
    campaign_starts_at?: string | null;
    campaign_ends_at?: string | null;
    campaign_active?: boolean;
    is_preview_active?: boolean;
  }>;
  revenue?: {
    booked_xcd: number;
    paid_xcd: number;
    pending_xcd: number;
    slot_count_filled: number;
    slot_count_total: number;
  };
}

const todayIso = () => new Date().toISOString().split('T')[0];
const isoPlus = (days: number) =>
  new Date(Date.now() + days * 86400000).toISOString().split('T')[0];

const defaultState: WizardState = {
  step: 0,
  selectedBusiness: null,
  newBusinessOpen: false,
  newBusinessStep: 0,
  campaignType: '',
  campaignTypes: [],
  packageKey: null,
  teamPreviewOnly: false,
  templateKey: '',
  templatePlacement: '',
  templateLabel: '',
  estimatedDailyImpressions: 400,
  creativeTab: 'ai',
  creativeMode: 'ai_generated',
  brandBrief: '',
  headline: '',
  body: '',
  ctaText: '',
  offerCode: '',
  offerTerms: '',
  backgroundColor: '#0f3d2e',
  aiCopyLoading: false,
  artStyleHint: '',
  artAspectRatio: 'banner',
  artLoading: false,
  artImageUrl: '',
  recentGenerations: [],
  uploadImageUrl: '',
  uploadVideoUrl: '',
  clientSubOption: 'logo',
  gallerySelectedLogoUrl: '',
  hourStart: 0,
  hourEnd: 24,
  corridorIds: [],
  festivalMode: false,
  eventSlug: '',
  eventLocationLabel: '',
  audience: 'all',
  weatherTriggers: [],
  targetRadiusKm: '',
  startDate: todayIso(),
  endDate: isoPlus(30),
  durationDays: 30,
  festivalQuickPick: '',
  bidXcd: '0.10',
  cpmRateXcd: '',
  flatFeeXcd: '',
  maxImpressionsPerDayPerUser: 5,
  coolDownHours: 24,
  maxDailyImpressions: '',
  invoiceStatus: 'invoiced',
  invoiceAmountXcd: '',
  pinLatOverride: null,
  pinLngOverride: null,
  pinStyle: null,
  pushAfterPublish: false,
  publishing: false,
  selectedFestival: null,
  festivalTier: null,
  festivalSubEventSlug: null,
  contractedAmountXcd: '',
  invoiceNumber: '',
};

// ─── Actions ────────────────────────────────────────────────────────────────

interface WizardActions {
  set: (partial: Partial<WizardState>) => void;
  reset: () => void;
  setStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  selectBusiness: (b: BusinessEntry) => void;
  selectTemplate: (t: SponsorTemplate) => void;
  addGeneration: (g: AiGeneration) => void;
  /**
   * Festival mode — pick a festival + tier in one shot. Auto-prefills
   * the rest of the wizard so the operator only needs to choose the
   * sponsoring brand and (optionally) refine creative / pricing.
   */
  selectFestivalAndTier: (
    festival: FestivalEntry,
    tier: FestivalTierKey,
    subEventSlug?: string | null,
  ) => void;
}

type WizardStore = WizardState & WizardActions;

// ─── Mode-keyed store map ───────────────────────────────────────────────────

const storeMap = new Map<WizardMode, StoreApi<WizardStore>>();

// 6-step wizard now: Brand(0) → Type(1) → Creative(2) → Targeting(3) → Schedule(4) → Review(5)
const MAX_STEP = 5;

function createWizardStore() {
  return create<WizardStore>((set) => ({
    ...defaultState,
    set: (partial) => set(partial as Partial<WizardState>),
    reset: () =>
      set({
        ...defaultState,
        startDate: todayIso(),
        endDate: isoPlus(defaultState.durationDays),
        pinLatOverride: null,
        pinLngOverride: null,
        pinStyle: null,
      }),
    setStep: (step) => set({ step: Math.max(0, Math.min(step, MAX_STEP)) }),
    nextStep: () => set((s) => ({ step: Math.min(s.step + 1, MAX_STEP) })),
    prevStep: () => set((s) => ({ step: Math.max(s.step - 1, 0) })),
    selectBusiness: (b) =>
      set({
        selectedBusiness: b,
        brandBrief: b.description || '',
        backgroundColor: b.brand_color_primary || '#0f3d2e',
        // Seed the pin override from the business profile. If the
        // directory entry has no coordinates we still seed Roseau
        // center so the live-preview map has something to render and
        // the operator can drag the marker to the real shop location.
        pinLatOverride: b.latitude ?? 15.301,
        pinLngOverride: b.longitude ?? -61.388,
        // Auto-derive the pin style from the business's category so
        // operators get a sensible default without having to think
        // about it. They can still override on the Creative step.
        pinStyle: resolvePinStyle(null, b.category).key,
        step: 1,
      }),
    selectTemplate: (t) =>
      set({
        templateKey: t.key,
        templatePlacement: t.placement,
        templateLabel: t.label,
        estimatedDailyImpressions: t.estimated_daily_impressions,
      }),
    addGeneration: (g) =>
      set((s) => ({
        recentGenerations: [g, ...s.recentGenerations].slice(0, 12),
        artImageUrl: g.imageUrl,
      })),
    selectFestivalAndTier: (festival, tier, subEventSlug = null) => {
      // Pull dates from the festival window. For tier='stage' with a
      // specific sub-event, use the sub-event's window so the campaign
      // is tightly scoped (e.g. "Calypso Monarch finals only").
      let windowStart = festival.starts_at;
      let windowEnd = festival.ends_at;
      if (subEventSlug) {
        const se = festival.sub_events.find((e) => e.slug === subEventSlug);
        if (se) {
          windowStart = se.starts_at;
          windowEnd = se.ends_at;
        }
      }
      // Default a sensible contracted amount from the tier's mid-band.
      // The operator can override on the publish step.
      const tierCfg = festival.sponsorship_tiers.find((t) => t.tier === tier);
      const midBand = tierCfg
        ? Math.round((tierCfg.rate_xcd_min + tierCfg.rate_xcd_max) / 2)
        : 0;
      // Calculate duration in whole days for the existing slider UI.
      const ms = new Date(windowEnd).getTime() - new Date(windowStart).getTime();
      const days = Math.max(1, Math.ceil(ms / 86400000));

      set({
        selectedFestival: festival,
        festivalTier: tier,
        festivalSubEventSlug: subEventSlug,
        // Auto-prefill creative
        backgroundColor: festival.theme_color,
        // The operator can replace these in the Creative step but
        // pre-seeding the festival's hero image + theme color makes
        // the live preview look real from step 2 onward.
        artImageUrl: festival.hero_image_url ?? '',
        // Auto-prefill targeting
        festivalMode: true,
        eventSlug: festival.slug,
        eventLocationLabel: festival.venue_name ?? '',
        corridorIds: festival.corridor_ids ?? [],
        // Auto-prefill schedule
        startDate: windowStart.split('T')[0],
        endDate: windowEnd.split('T')[0],
        durationDays: days,
        // Auto-prefill pricing default
        contractedAmountXcd: midBand > 0 ? String(midBand) : '',
        // Tier maps to a campaign template — title sponsors get
        // festival_takeover, stage/sub_event get takeover too,
        // corridor → commuter banner, venue_partner → sponsored map pin,
        // after_party → ride_complete_takeover. The wizard's existing
        // template picker still runs (step 1 in our flow) but this
        // pre-selection means most operators just click Next.
        campaignType: tierToCampaignType(tier),
        campaignTypes: [tierToCampaignType(tier)],
        // Advance to step 1 (the existing brand picker, now meaning
        // "WHO is sponsoring this festival slot").
        step: 1,
      });
    },
  }));
}

/**
 * Maps a festival sponsorship tier to the campaign template that best
 * realizes it. Title sponsors get a full festival takeover; corridor
 * sponsors get a commuter banner; venue partners get a sponsored map
 * pin; after-party sponsors get a ride-complete takeover. Stage /
 * sub-event sponsors share the takeover template with title sponsors.
 */
function tierToCampaignType(tier: FestivalTierKey): CampaignTypeKey {
  switch (tier) {
    case 'title':
    case 'stage':
    case 'sub_event':
      return 'festival_takeover';
    case 'corridor':
      return 'commuter_banner_morning';
    case 'venue_partner':
      return 'sponsored_map_pin';
    case 'after_party':
      return 'ride_complete_takeover';
    default:
      return 'festival_takeover';
  }
}

/**
 * Returns a Zustand store instance keyed by wizard mode.
 * Each mode (sponsor / festival / listing) gets its own isolated store.
 * This prevents state leaks between tabs.
 */
export function getWizardStore(mode: WizardMode): StoreApi<WizardStore> {
  if (!storeMap.has(mode)) {
    storeMap.set(mode, createWizardStore());
  }
  return storeMap.get(mode)!;
}

/**
 * React hook — use inside components.
 * Usage: const { step, headline, set } = useWizardStore('sponsor');
 */
export function useWizardStore(mode: WizardMode): WizardStore {
  const store = getWizardStore(mode);
  return useStore(store);
}

/**
 * React hook with selector — for performance-sensitive reads.
 * Usage: const step = useWizardStoreSelector('sponsor', s => s.step);
 */
export function useWizardStoreSelector<T>(mode: WizardMode, selector: (s: WizardStore) => T): T {
  const store = getWizardStore(mode);
  return useStore(store, selector);
}

export { defaultState };
